/**
 * The theatre's tag editor: the field's text, the suggestions under it, and the one write.
 *
 * Decides nothing. `model/exerciseTags` says what a press of Enter means and what is suggested;
 * `api/exerciseTagRequests` writes the whole set and patches the answer into the cached catalogue,
 * so the chips redraw from the row the card is already reading. The card is keyed by the movement,
 * so a walk to the next one starts this state again.
 *
 * The trainer's tag list (`GET /exercise/tags`) feeds the suggestions and their counts. Pending or
 * failed, it is an empty list: the field still adds what is typed, and the server still refuses
 * past the two-hundredth tag — the editor only loses the chance to say so first.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import { saveExerciseTags, tagRefusalOf, tagUsageQueryOptions } from '../api/exerciseTagRequests';
import {
  addTag,
  removeTag,
  tagSuggestions,
  type TagRefusal,
  type TagSuggestion,
} from '../model/exerciseTags';

/** What the editor says under the field: a named refusal, or a write that simply failed. */
export type TagEditorNotice = TagRefusal | 'failed';

export interface ExerciseTagEditor {
  readonly text: string;
  readonly setText: (text: string) => void;
  readonly suggestions: readonly TagSuggestion[];
  /** Add what was typed, or a suggestion's label. */
  readonly add: (label: string) => void;
  readonly remove: (label: string) => void;
  /** A write is on its way; the controls stay focusable and stop acting. */
  readonly busy: boolean;
  readonly notice: TagEditorNotice | null;
}

const NO_USAGE: readonly never[] = [];

export function useExerciseTagEditor(
  exerciseId: number,
  tags: readonly string[],
): ExerciseTagEditor {
  const client = useQueryClient();
  const lang = useContentLanguage();
  const usage = useQuery(tagUsageQueryOptions()).data ?? NO_USAGE;
  const [text, setText] = useState('');
  const [notice, setNotice] = useState<TagEditorNotice | null>(null);

  const save = useMutation({
    mutationFn: (labels: readonly string[]) => saveExerciseTags(client, exerciseId, labels, lang),
    onError: (error) => {
      setNotice(tagRefusalOf(error) ?? 'failed');
    },
  });

  const write = (labels: readonly string[], onSaved?: () => void) => {
    setNotice(null);
    save.mutate(labels, { onSuccess: onSaved });
  };

  return {
    text,
    setText: (next) => {
      setText(next);
      setNotice(null);
    },
    suggestions: tagSuggestions(text, tags, usage),
    add: (label) => {
      if (save.isPending) return;
      const addition = addTag(tags, label, usage);
      if (addition.kind === 'nothing') return;
      if (addition.kind === 'refused') {
        setNotice(addition.refusal);
        return;
      }
      if (addition.kind === 'already') {
        setText('');
        return;
      }
      write(addition.labels, () => {
        setText('');
      });
    },
    remove: (label) => {
      if (save.isPending) return;
      write(removeTag(tags, label));
    },
    busy: save.isPending,
    notice,
  };
}
